import { esc, kr, nf0, nf1, num } from '../calc/format.js';
import { heatGrid } from '../calc/heatmap.js';
import { WEEKDAYS } from '../calc/model.js';
import { el, emptyRow } from './dom.js';
import { helpButton } from './help.js';
import { state } from './state.js';

/* --------------------------------------------------------------------------
   Värmekartan: veckodag × klockslag

   En vanlig tabell med färgade rutor, inte ett diagram. Siffran står i varje
   ruta, så att den går att läsa utan att tolka en färgskala, och tabellen
   fungerar med skärmläsare och på telefon (rullar i sidled med veckodagen
   kvar till vänster). Färgen hjälper ögat att hitta topparna.

   Måttet är köp per dag som standard: det är antalet köp som avgör hur många
   som behövs i kassan. Omsättning per dag finns som andra mått.
   -------------------------------------------------------------------------- */

const pad = h => String(h).padStart(2, '0');
const span = h => `${pad(h)}–${pad((h + 1) % 24)}`;
const dayName = d => WEEKDAYS[d].toLowerCase();

/** Bakgrund för en ruta. Skalan stannar vid 60 % cyan så att navy text når 4,5:1. */
function shade(v, max){
  if(!(v > 0) || !(max > 0)) return '';
  return `background:rgba(0,168,226,${(0.06 + (v / max) * 0.54).toFixed(3)})`;
}

/** Antal köp per dag: med en decimal under 10, annars i hela köp. */
const count = v => (v >= 9.95 ? nf0 : nf1).format(v);
/** "1 köp", "2,5 köp" — köp böjs inte. */
const kop = v => count(v) + ' köp';

export function renderHeat(s, has){
  const t = el('tHeat'), peak = el('heatPeak'), legend = el('heatLegend');
  el('heatHelp').innerHTML = helpButton('tryck');
  const metric = el('heatMetric').value === 'revenue' ? 'revenue' : 'tx';
  const g = has ? heatGrid(state.view) : null;
  if(!g || !g.hours.length){
    t.innerHTML = emptyRow(1, has ? 'Ingen tidsdata' : 'Inga kvitton i urvalet', has ? undefined : 'Välj en annan period.');
    peak.hidden = true; legend.hidden = true;
    el('heatSub').textContent = 'Antal köp per veckodag och timme';
    return;
  }

  // Ett snitt av en enda dag är ingen genomsnittsdag; säg det som det är
  const single = [...g.dayCount.values()].every(n => n === 1);
  const key = metric === 'tx' ? 'txPerDay' : 'revenuePerDay';
  const max = g.max[key];
  const inThousands = metric === 'revenue' && max >= 10000;
  const fmt = metric === 'tx'
    ? count
    : v => nf0.format(Math.round(inThousands ? v / 1000 : v));
  const unit = metric === 'tx' ? 'köp' : (inThousands ? 'tusen kr' : 'kr');
  const what = metric === 'tx' ? 'Antal köp' : 'Omsättning';
  el('heatSub').textContent = single
    ? `${what} per veckodag och timme`
    : `${what} en genomsnittlig dag, per veckodag och timme`;

  // De tre timmarna med mest att göra, i ord och markerade i tabellen
  const busy = new Set(g.busiest.map(c => c.day * 24 + c.hour));
  peak.hidden = false;
  peak.innerHTML = 'Mest att göra i kassan: ' + g.busiest.map((c, i) => {
    const avg = single ? kop(c.txPerDay) : (i === 0 ? 'i snitt ' : '') + kop(c.txPerDay);
    const top = !single && c.maxTx > c.txPerDay ? `, som mest ${num(c.maxTx)}` : '';
    return `<b>${esc(dayName(c.day))} ${esc(span(c.hour))}</b> (${esc(avg + top)})`;
  }).join(', ') + '.';

  let head = `<thead><tr><th scope="col">Veckodag<small class="heat-unit">${esc(unit)}</small></th>` +
    g.hours.map(h => `<th scope="col" class="num">${pad(h)}</th>`).join('') +
    '<th scope="col" class="num">Hela dagen</th></tr></thead>';

  let body = '<tbody>';
  for(const d of g.days){
    const n = g.dayCount.get(d);
    const wd = WEEKDAYS[d];
    let dayTotal = 0;
    body += `<tr><th scope="row" class="name">${esc(wd)}<small class="heat-unit">${esc(
      n === 1 ? '1 dag' : num(n) + ' dagar')}</small></th>`;
    for(const h of g.hours){
      const c = g.cell(d, h);
      const v = c[key];
      dayTotal += v;
      const title = c.tx === 0
        ? `${wd} ${span(h)}: inga köp`
        : `${wd} ${span(h)}: ` + (metric === 'tx'
          ? (single ? kop(c.txPerDay) : `i snitt ${kop(c.txPerDay)}, som mest ${num(c.maxTx)} en dag`)
          : (single ? kr(c.revenuePerDay) : `i snitt ${kr(c.revenuePerDay)} per dag`));
      const cls = 'num' + (busy.has(d * 24 + h) ? ' is-peak' : '');
      body += `<td class="${cls}" style="${shade(v, max)}" title="${esc(title)}">${v > 0 ? esc(fmt(v)) : ''}</td>`;
    }
    body += `<td class="num"><b>${esc(metric === 'tx' ? fmt(dayTotal) : kr(dayTotal))}</b></td></tr>`;
  }
  body += '</tbody>';

  legend.hidden = false;
  // caption måste ligga först i tabellen för att vara giltig HTML
  t.innerHTML = `<caption class="vishidden">${esc(el('heatSub').textContent)}, ${esc(unit)}. ` +
    'De tre timmarna med flest köp är markerade.</caption>' + head + body;
}
